// Steps 6 and 7 - the Google verification codes and the DKIM values.
//
// One component, used twice. The two steps differ in exactly three ways - the
// column they write, the stage they run, and the wording - so they are one file
// parameterised by `field` and `stage` rather than two files that drift.
//
// Three rules run through this screen:
//
//   1. POSITION IS THE CONTRACT. A pasted spreadsheet column lands on the
//      domains at the SAME indexes it occupied in the sheet. An interior blank
//      cell is a blank value at its own index, never a reason to shift the
//      values below it up one row. See `spillPaste` below: it is a deliberate
//      mirror of the server helper of the same name.
//   2. A BLANK VALUE IS A SKIP. The stage writes nothing for that domain
//      (handleSingleTxt returns 'skipped' on empty content). It is never an
//      empty TXT record and it is never an error, so the grid says "skipped",
//      counts the blanks, and shows the count before the operator runs anything.
//   3. A BADGE COMES OFF THE PER-DOMAIN `*_status` COLUMN, never off
//      `last_error`. A successful stage legitimately leaves an advisory there.
//
// Autosave: local state is authoritative for any row the operator has touched,
// for the whole life of this mount. The shell re-reads the run every 3s while a
// stage is running, and that response can easily be older than the last
// keystroke; if the server's copy of the field were allowed to win, a poll
// landing between a keystroke and its save would silently revert what was typed.
// It cannot here - `typed` is only ever added to, and it is what the input
// renders. Nothing else in this feature writes these two columns, so no value
// the operator would want to see is being hidden by that rule.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClipboardEvent } from 'react';

import {
  fdApi,
  fdErrorMessage,
  type FdDomain,
  type FdStatus,
} from '../foreignDnsApi';
import {
  findStage,
  isZoneNotFound,
  summaryCount,
  type FdStepProps,
} from './shared';

type Field = 'verification_code' | 'dkim_value';
type Stage = 'verification' | 'dkim';

/** Long enough that a fast typist does not fire a request per character, short
 *  enough that "Saved" appears while the operator is still looking at the row. */
const DEBOUNCE_MS = 600;

/** Matches LINE_SPLIT in src/lib/foreign-cf-normalize.ts. A lone CR is a real
 *  line ending in legacy Excel and classic Mac exports; splitting on \n alone
 *  would collapse such a paste into a single value. */
const LINE_SPLIT = /\r\n|\r|\n/;

const HAS_LINE_BREAK = /\r|\n/;

/**
 * Mirror of `toPositionalLines` in src/lib/foreign-cf-normalize.ts.
 *
 * Trailing blank lines are dropped because a column copied out of Google Sheets
 * almost always ends in a newline, and that newline must not consume a field.
 * INTERIOR blanks are kept: a blank cell in the sheet is a domain the operator
 * has no code for, and it has to stay at its own index. Collapsing it would
 * shift every value below it up one row, which puts one customer's DKIM key on
 * another customer's domain - and nothing downstream catches that, because the
 * stage's read-back proves a record EXISTS, not that its value belongs there.
 */
export function positionalLines(text: string): string[] {
  const lines = text.split(LINE_SPLIT).map((l) => l.trim());
  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
  return lines;
}

/**
 * Mirror of `spillPaste` in src/lib/foreign-cf-normalize.ts, same signature and
 * same semantics: values carry the absolute index they land on, and `overflow`
 * counts the lines that had no field left to go into.
 *
 * The duplication is forced. Dockerfile.web builds the frontend in a stage that
 * copies only `frontend/`, so the backend module is not on disk at build time
 * and cannot be imported here. The two implementations are line-for-line
 * equivalent; if either changes, change both.
 */
export function spillPaste(
  pasted: string,
  startIndex: number,
  total: number,
): { values: { index: number; value: string }[]; overflow: number } {
  const lines = positionalLines(pasted);
  const start = Math.min(Math.max(0, startIndex), Math.max(0, total));
  const room = Math.max(0, total - start);
  const used = Math.min(lines.length, room);
  return {
    values: lines.slice(0, used).map((value, i) => ({ index: start + i, value })),
    overflow: lines.length - used,
  };
}

type SaveState =
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string };

const COPY: Record<
  Stage,
  {
    step: number;
    title: string;
    stageName: string;
    /** The record the stage writes, in the operator's language. */
    record: string;
    columnLabel: string;
    placeholder: string;
    hint: string;
    next: { step: number; label: string };
  }
> = {
  verification: {
    step: 6,
    title: 'Step 6 - Google verification codes',
    stageName: 'verification',
    record: 'a TXT record at the domain apex',
    columnLabel: 'Verification code',
    placeholder: 'blank = skipped',
    hint: 'Paste the whole column from your sheet into the first box. The google-site-verification= prefix is added for you if it is not already there, wrapping quotes are stripped, and anything pasted in front of the prefix (a record name, a TXT column) is dropped.',
    next: { step: 7, label: 'Continue to DKIM' },
  },
  dkim: {
    step: 7,
    title: 'Step 7 - DKIM',
    stageName: 'DKIM',
    record: 'a TXT record at google._domainkey',
    columnLabel: 'DKIM value',
    placeholder: 'blank = skipped',
    hint: 'Paste the whole column from your sheet into the first box. Adjacent quoted chunks are joined back into one value, wrapping quotes are stripped, and a leading google._domainkey record name is dropped.',
    next: { step: 8, label: 'Continue to the overview' },
  },
};

function fieldPatch(field: Field, value: string): { verification_code?: string; dkim_value?: string } {
  return field === 'verification_code' ? { verification_code: value } : { dkim_value: value };
}

/**
 * Does this raw cell normalise to nothing, and therefore get skipped?
 *
 * MIRRORS ONLY THE EMPTY CASE of normalizeVerificationCode and
 * normalizeDkimValue in src/lib/foreign-cf-normalize.ts - deliberately not the
 * whole normalisers, because a second copy of those would drift and would put a
 * wrong preview on screen. All this answers is the one question the counts need:
 * would `handleSingleTxt` see empty content and skip this domain?
 *
 * The case that matters in practice is a cell holding `""` - a pair of wrapping
 * quotes around nothing, which a copied spreadsheet cell produces easily. It
 * reads as filled to the naked eye and writes nothing, in both columns. For
 * DKIM, several empty quoted chunks (`"" ""`) collapse the same way.
 *
 * Note what is NOT empty: a DKIM cell holding only `google._domainkey`. The
 * record-name strip needs trailing whitespace to match and the value is trimmed
 * first, so that token survives as the content. Verified against the real
 * normalisers, not assumed.
 */
export function normalizesToEmpty(field: Field, raw: string): boolean {
  if (field === 'verification_code') {
    // normalizeVerificationCode returns '' iff the value is empty once trimmed
    // and unwrapped. Everything else gets the prefix and is never empty.
    return raw.trim().replace(/^"(.*)"$/s, '$1').trim() === '';
  }
  let value = raw.trim();
  const chunks = value.match(/"([^"]*)"/g);
  if (chunks && chunks.length > 0) value = chunks.map((c) => c.slice(1, -1)).join('');
  value = value.trim();
  value = value.replace(/^google\._domainkey\S*\s+/i, '').trim();
  return value === '';
}

/**
 * last_error on a row that did not fail is an advisory, not a failure. Same
 * rule and same rendering as the step 3/5 tables.
 *
 * The blank-skip wording is read off the RUN's own outcome - `handleSingleTxt`
 * writes the literal `'blank'` on the empty-content path - never off what is in
 * the input now, so it survives someone typing into a previously-skipped row
 * without re-running the stage.
 *
 * It used to be INFERRED from (status === 'skipped', message === null, the row
 * has a zone), because the empty-content path wrote null. That erased whatever
 * the previous stage had recorded in what is ONE column shared by every stage,
 * so a zoneless domain's `zone_not_found` came back reading as a clean blank
 * skip. The backend now names the reason and this reads it.
 */
function messageCell(status: FdStatus, message: string | null): JSX.Element {
  if (message === 'blank') {
    return <td className="fd-cell-faint">left blank, nothing was written</td>;
  }
  if (message === null) return <td className="fd-cell-faint">-</td>;
  return <td className={status === 'failed' ? 'fd-cell-error' : 'fd-cell-faint'}>{message}</td>;
}

export function StepCodeGrid(
  props: FdStepProps & { field: Field; stage: Stage },
): JSX.Element {
  const { runId, detail, reload, notifyEnqueued, goToStep, toast, field, stage } = props;
  const { domains, stages } = detail;
  const copy = COPY[stage];

  const stageRow = findStage(stages, stage);
  const running = stageRow?.status === 'running';

  /** Values the operator has entered in this mount. Authoritative over the
   *  server copy for every id it holds - see the file header. */
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<Record<string, SaveState>>({});
  const [overflow, setOverflow] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Debounce timers, values waiting to be written, and a per-row change
   *  counter. The counter is bumped on every keystroke and captured by each
   *  save; a response whose counter is stale is discarded, so a row can never
   *  read "Saved" while a newer keystroke is still unwritten. */
  const timers = useRef<Record<string, number>>({});
  const pending = useRef<Record<string, string>>({});
  const seq = useRef<Record<string, number>>({});
  /** Per-row promise chain. Two saves for the same row never overlap, so the
   *  last value the operator typed is the last value the server receives. */
  const chain = useRef<Record<string, Promise<void>>>({});

  /**
   * Rows whose LAST completed save failed, id -> message, cleared the moment a
   * save for that row succeeds.
   *
   * This is the gate on Run, and it has to be a ref rather than the `saveState`
   * render snapshot. A save that was already in flight when the operator clicked
   * Run is not in `pending`, so flushAll cannot report on it, and its failure
   * lands in React state AFTER the click has read that state - `apply` would
   * enqueue on a stale "everything is fine", the stage would write the row's
   * PREVIOUS database value, and the read-back would confirm a record exists and
   * turn the row green while the input on screen shows something else. Same
   * class of silent wrongness as a shifted paste. A ref is readable after the
   * await, which is the only moment the answer is true.
   *
   * Recorded unconditionally, not behind the `seq` guard: saves for one row are
   * strictly serialised by `chain`, so the last save to complete is always the
   * last one issued, and its verdict is the row's verdict.
   */
  const failed = useRef<Record<string, string>>({});

  const serverValue = useCallback(
    (d: FdDomain): string =>
      (field === 'verification_code' ? d.verification_code : d.dkim_value) ?? '',
    [field],
  );
  const valueOf = useCallback(
    (d: FdDomain): string => typed[d.id] ?? serverValue(d),
    [typed, serverValue],
  );

  const runChained = useCallback((ids: string[], task: () => Promise<void>): Promise<void> => {
    const prior = Promise.all(ids.map((id) => chain.current[id] ?? Promise.resolve()));
    const next = prior.then(task);
    const settled = next.then(
      () => undefined,
      () => undefined,
    );
    for (const id of ids) chain.current[id] = settled;
    return next;
  }, []);

  /** Bump the row's change counter and show it as unsaved. Called on every
   *  keystroke, every spilled paste and every retry. */
  const markDirty = useCallback((id: string): number => {
    const next = (seq.current[id] ?? 0) + 1;
    seq.current[id] = next;
    return next;
  }, []);

  const clearTimer = useCallback((id: string): void => {
    const timer = timers.current[id];
    if (timer !== undefined) {
      window.clearTimeout(timer);
      delete timers.current[id];
    }
  }, []);

  const saveNow = useCallback(
    (id: string, value: string, mySeq: number): Promise<void> =>
      runChained([id], async () => {
        try {
          await fdApi.saveDomainField(runId, id, fieldPatch(field, value));
          delete failed.current[id];
          if (seq.current[id] === mySeq) {
            setSaveState((s) => ({ ...s, [id]: { kind: 'saved' } }));
          }
        } catch (err) {
          const message = fdErrorMessage(err);
          failed.current[id] = message;
          if (seq.current[id] === mySeq) {
            setSaveState((s) => ({ ...s, [id]: { kind: 'error', message } }));
          }
        }
      }),
    [runId, field, runChained],
  );

  /** Write this row now instead of when its debounce expires. */
  const flushOne = useCallback(
    (id: string): Promise<void> => {
      clearTimer(id);
      if (!(id in pending.current)) return Promise.resolve();
      const value = pending.current[id];
      delete pending.current[id];
      return saveNow(id, value, seq.current[id] ?? markDirty(id));
    },
    [clearTimer, saveNow, markDirty],
  );

  /**
   * Everything outstanding, written and settled; then the ids of the rows whose
   * last save failed.
   *
   * Called before the stage is enqueued, because the worker reads these columns
   * out of the database - a job started 200ms after a paste would otherwise
   * write the previous values.
   *
   * The return value is read AFTER both awaits, deliberately. Awaiting only the
   * rows still in `pending` misses a save whose debounce had already fired, and
   * awaiting the chains is not enough on its own either: `saveNow` swallows the
   * rejection into state, so every chain resolves whether the PATCH worked or
   * not. `failed.current` is the only thing that still knows, and it is only
   * true once nothing is in flight.
   */
  const flushAll = useCallback(async (): Promise<string[]> => {
    const ids = Object.keys(pending.current);
    await Promise.all(ids.map((id) => flushOne(id)));
    await Promise.all(Object.values(chain.current));
    return Object.keys(failed.current);
  }, [flushOne]);

  const onChange = useCallback(
    (id: string, value: string): void => {
      setTyped((t) => ({ ...t, [id]: value }));
      pending.current[id] = value;
      // The overflow notice is about the shape of ONE paste against the list as
      // it stood. Once the grid is being edited again it is stale advice, so it
      // goes rather than sitting there until the next paste happens to replace
      // it. The Dismiss control below covers the read-it-and-move-on case.
      setOverflow(0);
      const mySeq = markDirty(id);
      setSaveState((s) => ({ ...s, [id]: { kind: 'saving' } }));
      clearTimer(id);
      timers.current[id] = window.setTimeout(() => {
        delete timers.current[id];
        if (!(id in pending.current)) return;
        const latest = pending.current[id];
        delete pending.current[id];
        void saveNow(id, latest, mySeq);
      }, DEBOUNCE_MS);
    },
    [markDirty, clearTimer, saveNow],
  );

  const onPaste = useCallback(
    (index: number) => (e: ClipboardEvent<HTMLInputElement>): void => {
      const text = e.clipboardData.getData('text');
      // No line break at all: an ordinary single-value paste, possibly onto a
      // selection inside the box. The browser does that better than we would.
      if (!HAS_LINE_BREAK.test(text)) return;
      e.preventDefault();
      const spill = spillPaste(text, index, domains.length);
      setOverflow(spill.overflow);
      if (spill.values.length === 0) return;

      const updates = spill.values.map((v) => ({ id: domains[v.index].id, value: v.value }));
      const seqs = new Map<string, number>();
      for (const u of updates) {
        // The bulk write supersedes anything this row had queued.
        clearTimer(u.id);
        delete pending.current[u.id];
        seqs.set(u.id, markDirty(u.id));
      }
      setTyped((t) => {
        const next = { ...t };
        for (const u of updates) next[u.id] = u.value;
        return next;
      });
      setSaveState((s) => {
        const next = { ...s };
        for (const u of updates) next[u.id] = { kind: 'saving' };
        return next;
      });

      void runChained(
        updates.map((u) => u.id),
        async () => {
          try {
            // All-or-nothing at the route (DOMAIN_NOT_IN_RUN rejects the whole
            // body before anything is written), so one verdict covers every row.
            await fdApi.saveDomainFieldsBulk(runId, field, updates);
            for (const u of updates) delete failed.current[u.id];
            setSaveState((s) => {
              const next = { ...s };
              for (const u of updates) {
                if (seq.current[u.id] === seqs.get(u.id)) next[u.id] = { kind: 'saved' };
              }
              return next;
            });
          } catch (err) {
            const message = fdErrorMessage(err);
            for (const u of updates) failed.current[u.id] = message;
            setSaveState((s) => {
              const next = { ...s };
              for (const u of updates) {
                if (seq.current[u.id] === seqs.get(u.id)) next[u.id] = { kind: 'error', message };
              }
              return next;
            });
          }
        },
      );
    },
    [domains, runId, field, clearTimer, markDirty, runChained],
  );

  /**
   * Anything still on a debounce when this grid goes away is written on the way
   * out. Without it, typing into the last row and immediately clicking a rail
   * step would drop that row - the value would still be on screen from the
   * server's copy nowhere, and the operator would have no reason to suspect it.
   * Deliberately not routed through saveNow: this component is gone, so there
   * is no state left to update.
   */
  useEffect(() => {
    const timerMap = timers.current;
    const pendingMap = pending.current;
    return () => {
      for (const id of Object.keys(timerMap)) window.clearTimeout(timerMap[id]);
      const leftover = Object.entries(pendingMap);
      for (const id of Object.keys(pendingMap)) delete pendingMap[id];
      for (const [id, value] of leftover) {
        void fdApi.saveDomainField(runId, id, fieldPatch(field, value)).catch(() => undefined);
      }
    };
  }, [runId, field]);

  // Counted AFTER normalisation, because that is what decides whether a domain
  // is written or skipped. A cell holding only `""` looks filled and writes
  // nothing; counting the raw string would have the toast promise a record that
  // never gets created.
  const filled = domains.filter((d) => !normalizesToEmpty(field, valueOf(d))).length;
  const blank = domains.length - filled;
  const noZone = domains.filter(isZoneNotFound).length;
  const unsaved = Object.values(saveState).filter((s) => s.kind === 'error').length;
  const ok = summaryCount(stageRow, 'ok');
  const failedCount = summaryCount(stageRow, 'failed');
  const skippedCount = summaryCount(stageRow, 'skipped');

  async function apply(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      // Everything outstanding is written and settled FIRST, and the answer is
      // read after the await - never from the render snapshot the click was made
      // on. A save that was already in flight at click time is not in `pending`
      // and its failure has not reached React state yet, so a pre-click check
      // would wave it through: the stage would then write that row's previous
      // database value, the read-back would confirm a record exists, and the row
      // would go green while the input shows something else entirely.
      const stillFailed = await flushAll();
      if (stillFailed.length > 0) {
        const names = domains
          .filter((d) => stillFailed.includes(d.id))
          .map((d) => d.domain)
          .join(', ');
        setError(
          `${stillFailed.length} row(s) could not be saved, so the stage was not started: ${names}. Retry them first - running now would write their previous values while the screen shows the new ones.`,
        );
        return;
      }
      const res = await fdApi.runStage(runId, stage);
      if (res.alreadyRunning) {
        toast('warn', `The ${copy.stageName} stage is already running - nothing new was started.`);
      } else {
        notifyEnqueued();
        toast(
          'success',
          `Writing ${filled} ${copy.stageName} record(s). ${blank} blank row(s) are skipped.`,
        );
      }
      await reload();
    } catch (err) {
      setError(fdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function retry(d: FdDomain): void {
    const value = valueOf(d);
    const mySeq = markDirty(d.id);
    setSaveState((s) => ({ ...s, [d.id]: { kind: 'saving' } }));
    void saveNow(d.id, value, mySeq);
  }

  function statusOf(d: FdDomain): FdStatus {
    return field === 'verification_code' ? d.verification_status : d.dkim_status;
  }

  return (
    <section className="fd-step">
      <div className="fd-step-head">
        <h2>{copy.title}</h2>
        <p>
          One value per domain, in the order the domains are listed. Each one is written as{' '}
          {copy.record} on that domain's zone, and the stage turns a row green only after a fresh
          read-back from Cloudflare finds it. A row left blank is skipped - nothing is written for
          that domain and it is not a failure.
        </p>
      </div>

      <div className="fd-step-body">
        <div className="fd-notice">
          {filled} of {domains.length} will be written.{' '}
          {blank === 0
            ? 'No blank rows, so every domain gets a record.'
            : `${blank} row(s) are empty once normalised and will be skipped - nothing is written for those domains.`}
          {noZone > 0 &&
            ` ${noZone} domain(s) have no Cloudflare zone and are skipped by every stage regardless.`}
        </div>

        <span className="fd-hint">{copy.hint}</span>

        {overflow > 0 && (
          <div className="fd-notice fd-notice--warn">
            <div className="fd-actions">
              <span>
                {overflow} pasted line(s) had no field to go into. Check that your domain list and
                your sheet are in the same order.
              </span>
              <div className="fd-actions-spacer" />
              <button
                type="button"
                className="fd-btn fd-btn--ghost fd-btn--small"
                onClick={() => setOverflow(0)}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {unsaved > 0 && (
          <div className="fd-notice fd-notice--err">
            {unsaved} row(s) are not saved. Use Retry on each one - until it reads Saved, the value
            on screen is not the value the stage will write.
          </div>
        )}

        {error && <div className="fd-notice fd-notice--err">{error}</div>}

        {running && (
          <div className="fd-notice fd-notice--info">
            Writing {copy.record} to {filled} domain(s) and reading each zone back. This page updates
            itself.
          </div>
        )}

        {!running && stageRow?.status === 'done' && (
          <div className="fd-notice">
            {copy.stageName} finished
            {ok === null ? '' : `: ${ok} ok`}
            {skippedCount === null || skippedCount === 0 ? '' : `, ${skippedCount} skipped`}
            {failedCount === null || failedCount === 0 ? '' : `, ${failedCount} failed`}.
          </div>
        )}

        {!running && stageRow?.status === 'failed' && (
          <div className="fd-notice fd-notice--err">
            The {copy.stageName} stage failed: {stageRow.error_message ?? 'no message recorded'}
          </div>
        )}

        <div className="fd-field">
          <span className="fd-field-label">
            {copy.columnLabel} per domain - paste a whole column into any box and it fills down from
            there
          </span>
          <div>
            {domains.map((d, index) => {
              const value = valueOf(d);
              const state = saveState[d.id];
              return (
                <div className="fd-grid-row" key={d.id}>
                  <span className="fd-grid-pos">{d.position + 1}</span>
                  <span className="fd-grid-domain" title={d.domain}>
                    {d.domain}
                  </span>
                  <input
                    className="fd-input"
                    type="text"
                    value={value}
                    aria-label={`${copy.columnLabel} for ${d.domain}`}
                    placeholder={copy.placeholder}
                    spellCheck={false}
                    autoComplete="off"
                    disabled={running}
                    onChange={(e) => onChange(d.id, e.target.value)}
                    onBlur={() => void flushOne(d.id)}
                    onPaste={onPaste(index)}
                  />
                  <span className={`fd-badge fd-badge--${statusOf(d)}`}>{statusOf(d)}</span>
                  <span className="fd-grid-status">
                    {state?.kind === 'saving' && (
                      <span className="fd-saved fd-saved--on fd-saved--pending">Saving...</span>
                    )}
                    {state?.kind === 'saved' && <span className="fd-saved fd-saved--on">Saved</span>}
                    {state?.kind === 'error' && (
                      <>
                        <span
                          className="fd-saved fd-saved--on fd-saved--err"
                          title={state.message}
                        >
                          Not saved
                        </span>
                        <button
                          type="button"
                          className="fd-btn fd-btn--ghost fd-btn--small"
                          onClick={() => retry(d)}
                        >
                          Retry
                        </button>
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="fd-actions">
          <button
            type="button"
            className="fd-btn fd-btn--primary"
            onClick={() => void apply()}
            disabled={busy || running || filled === 0}
          >
            {running ? 'Running...' : `Run ${copy.stageName}`}
          </button>
          <div className="fd-actions-spacer" />
          <button
            type="button"
            className="fd-btn fd-btn--secondary"
            onClick={() => void goToStep(copy.next.step)}
            disabled={busy}
          >
            {copy.next.label}
          </button>
        </div>

        {filled === 0 && (
          <div className="fd-notice fd-notice--warn">
            Every row is blank, so this stage would skip every domain. Paste the column from your
            sheet first.
          </div>
        )}
      </div>

      {stageRow !== null && (
        <div className="fd-table-wrap">
          <table className="fd-table">
            <thead>
              <tr>
                <th className="fd-col-num">#</th>
                <th>Domain</th>
                <th className="fd-col-center">{copy.columnLabel}</th>
                <th>Last message</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => {
                const status = statusOf(d);
                return (
                  <tr key={d.id}>
                    <td className="fd-col-num fd-cell-faint">{d.position + 1}</td>
                    <td className="fd-cell-mono">{d.domain}</td>
                    <td className="fd-col-center">
                      <span className={`fd-badge fd-badge--${status}`}>{status}</span>
                    </td>
                    {messageCell(status, d.last_error)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {stageRow !== null && (
        <div className="fd-step-body">
          <span className="fd-hint">
            <strong>Last message</strong> is one shared column per domain
            (<span className="fd-mono">last_error</span>), overwritten by whichever stage touched
            that domain most recently, so the text here can belong to another step. It is also not
            always a failure - the badge beside it is this step's own verdict and is the authority.
          </span>
        </div>
      )}
    </section>
  );
}
